class GiftManager {
  constructor() {
    this.gifts = window.gifts || [];
    this.currentCart = null;
    this.isProcessing = false;
    this.justRemovedProductGift = null;
    this.cartItems = null; // Reference to CartItems instance

    // Bind methods
    this.updateGifts = this.updateGifts.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);

    // Listen for cart updates via pub/sub system
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
  }

  /**
   * Main method to sync gifts with cart value
   * @param {Object} cart - Current cart object
   * @returns {Promise}
   */
  async updateGifts(cart = null) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      // Get current cart if not provided
      if (!cart) {
        cart = await this.fetchCart();
      }

      // Store previous cart total for comparison
      const previousCartTotal = this.currentCart ? this.calculateCartTotal(this.currentCart) : 0;
      this.currentCart = cart;

      // Calculate cart total (excluding gifts)
      const cartTotal = this.calculateCartTotal(cart);

      // Check for manual gift selection and validate it
      let manualSelection = this.getManualGiftSelection();
      const currentGift = this.getCurrentGiftInCart(cart);

      console.log('Gift Manager: Update gifts flow:', {
        hasManualSelection: !!manualSelection,
        manualSelectionVariant: manualSelection?.variantId,
        currentGiftVariant: currentGift?.variant_id,
        cartTotal,
        cartTotalFormatted: (cartTotal / 100).toFixed(2)
      });

      // Show all available gift tiers for debugging
      console.log('Gift Manager: All available gift tiers:', this.gifts.map(gift => ({
        variant: gift.variant,
        title: gift.title,
        minAmount: gift.min_amount,
        minAmountFormatted: (gift.min_amount / 100).toFixed(2),
        isEligible: cartTotal >= gift.min_amount,
        tier: gift.min_amount <= 10000 ? 'Tier 1' : gift.min_amount <= 20000 ? 'Tier 2' : 'Tier 3'
      })).sort((a, b) => a.minAmount - b.minAmount));

      // Validate manual selection - clear if the gift no longer exists
      if (manualSelection) {
        const selectedGift = this.gifts.find(gift => gift.variant === manualSelection.variantId);
        if (!selectedGift) {
          console.log('Gift Manager: Manual selection invalid - gift no longer exists');
          await this.clearManualGiftSelection();
          manualSelection = null;
        }
      }

      if (manualSelection) {
        console.log('Gift Manager: Using manual selection logic');
        // User has manually selected a gift - respect their choice
        await this.handleManualGiftSelection(manualSelection, currentGift, cartTotal);
      } else {
        console.log('Gift Manager: Using automatic selection logic');
        // No manual selection - use automatic logic
        await this.handleAutomaticGiftSelection(cartTotal, currentGift);
      }

      // Trigger UI updates - always update progress bar with latest cart data
      this.updateProgressBar();

    } catch (error) {
      console.error('Gift management error:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Fetch current cart data
   * @returns {Promise<Object>}
   */
  async fetchCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.json();
  }



  /**
   * Calculate cart total excluding gifts
   * @param {Object} cart - Cart object
   * @returns {number}
   */
  calculateCartTotal(cart) {
    return cart.items.reduce((total, item) => {
      if (!item.properties || !item.properties['_gift_product']) {
        total += item.line_price;
      }
      return total;
    }, 0);
  }

  /**
   * Get sections to render for cart updates (similar to CartItems)
   * @returns {Array}
   */
  getSectionsToRender() {
    // Use CartItems instance if available, otherwise return basic sections
    if (this.cartItems && this.cartItems.getSectionsToRender) {
      return this.cartItems.getSectionsToRender();
    }

    // Fallback sections for gift manager updates
    const sections = [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset?.id,
        selector: '.js-contents'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset?.id,
        selector: '.js-contents-totals'
      },
      {
        id: 'main-cart-shipping',
        section: document.getElementById('main-cart-shipping')?.dataset?.id,
        selector: '.js-contents-shipping'
      }
    ];

    // Safely add product-gifts section if it exists
    const productGiftsElement = document.getElementById('product-gifts') ||
      document.querySelector('[data-product-gifts]')?.closest('.product__gifts');

    if (productGiftsElement) {
      // Determine correct selector based on context
      let selector = '.shopify-section'; // Default for standalone section

      if (productGiftsElement.closest('.section-product-gifts')) {
        selector = '.section-product-gifts';
      } else if (productGiftsElement.id === 'product-gifts') {
        selector = null; // Will use getElementById fallback
      }

      sections.push({
        id: 'product-gifts',
        section: 'product-gifts',
        selector: selector
      });
    }

    return sections.filter(section => section.section); // Filter out null sections
  }

  /**
   * Handle manual gift selection logic
   * @param {Object} manualSelection - Manual selection object
   * @param {Object} currentGift - Current gift in cart
   * @param {number} cartTotal - Cart total in cents
   * @returns {Promise}
   */
  async handleManualGiftSelection(manualSelection, currentGift, cartTotal) {
    const selectedGift = this.gifts.find(gift => gift.variant === manualSelection.variantId);

    if (!selectedGift) {
      // Manual selection is no longer valid (gift doesn't exist)
      await this.clearManualGiftSelection();
      await this.handleAutomaticGiftSelection(cartTotal, currentGift);
      return;
    }

    if (cartTotal < selectedGift.min_amount) {
      // Cart total too low for manually selected gift - clear selection and remove gift
      await this.clearManualGiftSelection();
      if (currentGift) {
        await this.removeGiftFromCart(currentGift, false, 'auto');
      }
      // Let automatic logic handle what to do next
      await this.handleAutomaticGiftSelection(cartTotal, null);
      return;
    }

    // Check if there's a newly unlocked higher tier (only upgrade if it's actually NEW)
    const bestAvailableNow = this.findBestEligibleGift(cartTotal);

    if (bestAvailableNow && bestAvailableNow.min_amount > selectedGift.min_amount && manualSelection.selectionCartTotal) {
      // Check what was the best available when user made their selection
      const bestAtSelection = this.findBestEligibleGift(manualSelection.selectionCartTotal);
      const wasNewTierUnlocked = !bestAtSelection || bestAvailableNow.min_amount > bestAtSelection.min_amount;

      if (wasNewTierUnlocked) {
        // A genuinely NEW higher tier is now available - auto-upgrade
        console.log('Gift Manager: Auto-upgrading manual selection - new tier unlocked:', {
          from: selectedGift.variant,
          to: bestAvailableNow.variant,
          fromTier: selectedGift.min_amount,
          toTier: bestAvailableNow.min_amount,
          selectionCartTotal: manualSelection.selectionCartTotal,
          bestAtSelection: bestAtSelection?.min_amount
        });

        // Clear manual selection and upgrade to new tier
        await this.clearManualGiftSelection();

        if (currentGift) {
          await this.replaceGift(currentGift, bestAvailableNow, 'auto');
        } else {
          await this.addGiftToCart(bestAvailableNow);
        }
        return;
      } else {
        console.log('Gift Manager: Keeping manual selection - higher tier was already available when selected');
      }
    }

    // Manual selection is valid - ensure it's in cart
    if (!currentGift || currentGift.variant_id !== selectedGift.variant) {
      console.log('Gift Manager: Manual selection needs cart update:', {
        currentGiftVariant: currentGift?.variant_id,
        selectedGiftVariant: selectedGift.variant,
        action: currentGift ? 'replace' : 'add'
      });

      if (currentGift) {
        await this.replaceGift(currentGift, selectedGift, 'manual');
      } else {
        await this.addGiftToCart(selectedGift);
      }
    } else {
      console.log('Gift Manager: Manual selection already correct in cart');
    }
  }

  /**
   * Handle automatic gift selection logic
   * @param {number} cartTotal - Cart total in cents
   * @param {Object} currentGift - Current gift in cart
   * @returns {Promise}
   */
  async handleAutomaticGiftSelection(cartTotal, currentGift) {
    // Find eligible gift using existing logic
    const eligibleGift = this.findEligibleGift(cartTotal);

    console.log('Gift Manager: Automatic selection analysis:', {
      cartTotal,
      cartTotalFormatted: (cartTotal / 100).toFixed(2),
      currentGiftVariant: currentGift?.variant_id,
      eligibleGiftVariant: eligibleGift?.variant,
    });

    // Normal automatic behavior when no manual removals
    // else if (eligibleGift && !currentGift) {
    if (eligibleGift && !currentGift) {
      await this.addGiftToCart(eligibleGift);
    } else if (eligibleGift && currentGift && eligibleGift.variant !== currentGift.variant_id) {
      // Always replace with the best eligible gift (handles both upgrades and downgrades)
      await this.replaceGift(currentGift, eligibleGift, 'auto');
    } else if (!eligibleGift && currentGift) {
      await this.removeGiftFromCart(currentGift, false, 'auto');
    }
  }



  /**
   * Find the best eligible gift (ignoring removed gifts, for upgrade comparison)
   * @param {number} cartTotal - Cart total in cents
   * @returns {Object|null}
   */
  findBestEligibleGift(cartTotal) {
    if (!this.gifts.length) return null;

    let bestGift = null;
    let maxAmount = 0;

    for (const gift of this.gifts) {
      if (cartTotal >= gift.min_amount && gift.min_amount > maxAmount) {
        maxAmount = gift.min_amount;
        bestGift = gift;
      }
    }

    return bestGift;
  }



  /**
   * Find the highest value gift that user qualifies for
   * @param {number} cartTotal - Cart total in cents
   * @returns {Object|null}
   */
  findEligibleGift(cartTotal) {
    if (!this.gifts.length) {
      return null;
    }

    let eligibleGift = null;
    let maxAmount = 0;

    for (const gift of this.gifts) {
      const minAmount = gift.min_amount; // Already in cents

      if (cartTotal >= minAmount && minAmount > maxAmount) {
        maxAmount = minAmount;
        eligibleGift = gift;
      }
    }

    if (eligibleGift) {
      console.log('Gift Manager: Selected eligible gift:', eligibleGift.variant, eligibleGift.title);
    } else {
      console.log('Gift Manager: No eligible gifts found for cart total:', cartTotal);
    }

    return eligibleGift;
  }

  /**
   * Get current gift item from cart
   * @param {Object} cart - Cart object
   * @returns {Object|null}
   */
  getCurrentGiftInCart(cart) {
    // Find ALL gift items first to see if there are multiple
    const allGiftItems = cart.items.filter(item =>
      item.properties && item.properties['_gift_product']
    );

    console.log('Gift Manager: Found', allGiftItems.length, 'gift items in cart');

    if (allGiftItems.length > 1) {
      console.log('Gift Manager: WARNING - Multiple gifts found:', allGiftItems.map(item => ({
        variant_id: item.variant_id,
        title: item.title || item.product_title
      })));
    }

    const giftItem = allGiftItems[0] || null;

    // Debug logging to see what gift item looks like
    if (giftItem) {
      console.log('Gift Manager: Using gift item:', {
        variant_id: giftItem.variant_id,
        id: giftItem.id,
        key: giftItem.key,
        title: giftItem.title || giftItem.product_title,
        properties: giftItem.properties
      });
    } else {
      console.log('Gift Manager: No gift items found in cart');
    }

    return giftItem;
  }

  /**
   * Get list of manually removed gift variant IDs from cart attributes
   * @returns {Array<string>}
   */
  getRemovedGifts() {
    if (!this.currentCart?.attributes) return [];

    const removedGiftsAttr = this.currentCart.attributes['_removed_gifts'];
    if (!removedGiftsAttr) return [];

    try {
      return removedGiftsAttr.split(',').filter(id => id.trim());
    } catch (error) {
      console.error('Error parsing removed gifts:', error);
      return [];
    }
  }

  /**
   * Add gift variant ID to removed gifts list in cart attributes
   * @param {string|number} variantId - Variant ID to add to removed list
   * @returns {Promise}
   */
  async addToRemovedGifts(variantId) {
    const removedGifts = this.getRemovedGifts();
    const variantIdStr = variantId.toString();

    if (!removedGifts.includes(variantIdStr)) {
      removedGifts.push(variantIdStr);

      console.log('Gift Manager: Adding to removed gifts list:', variantIdStr);
      console.log('Gift Manager: Updated removed gifts list:', removedGifts);

      await this.updateCartAttributes({
        '_removed_gifts': removedGifts.join(',')
      });
    }
  }

  /**
   * Remove gift variant ID from removed gifts list in cart attributes
   * @param {string|number} variantId - Variant ID to remove from removed list
   * @returns {Promise}
   */
  async removeFromRemovedGifts(variantId) {
    const removedGifts = this.getRemovedGifts();
    const variantIdStr = variantId.toString();

    if (removedGifts.includes(variantIdStr)) {
      const updatedRemovedGifts = removedGifts.filter(id => id !== variantIdStr);

      console.log('Gift Manager: Removing from removed gifts list:', variantIdStr);
      console.log('Gift Manager: Updated removed gifts list:', updatedRemovedGifts);

      await this.updateCartAttributes({
        '_removed_gifts': updatedRemovedGifts.join(',')
      });
    }
  }



  /**
   * Update cart attributes
   * @param {Object} attributes - Attributes to update
   * @returns {Promise}
   */
  async updateCartAttributes(attributes) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributes: attributes
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to update cart attributes: ${response.statusText}`);
    }

    const result = await response.json();
    this.currentCart = result; // Update current cart with new attributes

    return result;
  }

  /**
   * Add gift to cart
   * @param {Object} gift - Gift object
   * @param {boolean} skipEvent - Whether to skip publishing cart update event
   * @returns {Promise}
   */
  async addGiftToCart(gift, skipEvent = false) {
    try {
      const sectionsToRender = this.getSectionsToRender();
      const sections = sectionsToRender.map(section => section.section);

      // Enable loading for all gift items immediately before API call
      this.enableAllGiftLoading();

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: gift.variant,
            quantity: 1,
            properties: {
              '_gift_product': {
                'price': gift.price,
                'title': gift.title
              }
            }
          }],
          sections: sections,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to add gift: ${response.statusText}`);
      }

      const result = await response.json();

      // Handle direct section updates for gift manager
      this.updateSections(result.sections, sectionsToRender);

      // Publish cart update event with response data (unless skipped)
      if (!skipEvent) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'gift-manager',
          cartData: result,
          sections: result.sections,
          sectionsToRender: sectionsToRender
        });
      }

      return result;
    } catch (error) {
      // Disable loading on error
      this.disableAllGiftLoading();
      throw error;
    }
  }

  /**
   * Remove gift from cart
   * @param {Object} giftItem - Gift cart item
   * @param {boolean} skipEvent - Whether to skip publishing cart update event
   * @param {string} reason - Reason for removal ('auto' or 'manual')
   * @returns {Promise}
   */
  async removeGiftFromCart(giftItem, skipEvent = false, reason = 'auto') {
    try {
      // Mark this as an automatic removal so it won't be tracked as manual
      this.lastRemovalReason = reason;

      const sectionsToRender = this.getSectionsToRender();
      const sections = sectionsToRender.map(section => section.section);

      // Enable loading for the specific gift being removed immediately before API call
      this.enableGiftLoading(giftItem.variant_id);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0,
          sections: sections,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to remove gift: ${response.statusText}`);
      }

      const result = await response.json();

      // Handle direct section updates for gift manager
      this.updateSections(result.sections, sectionsToRender);

      // Publish cart update event with response data (unless skipped)
      if (!skipEvent) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'gift-manager',
          cartData: result,
          sections: result.sections,
          sectionsToRender: sectionsToRender
        });
      }

      return result;
    } catch (error) {
      // Disable loading on error
      this.disableGiftLoading(giftItem.variant_id);
      throw error;
    }
  }

  /**
   * Replace current gift with a better one
   * @param {Object} currentGift - Current gift cart item
   * @param {Object} newGift - New gift object
   * @param {string} reason - Reason for replacement ('auto' or 'manual')
   * @returns {Promise}
   */
  async replaceGift(currentGift, newGift, reason = 'auto') {
    try {
      // Enable loading for current gift being replaced immediately
      this.enableGiftLoading(currentGift.variant_id);

      // Remove old gift and add new gift without triggering individual events
      // Mark removal reason appropriately
      await this.removeGiftFromCart(currentGift, true, reason);
      const addResult = await this.addGiftToCart(newGift, true);

      // Handle direct section updates for gift manager
      const sectionsToRender = this.getSectionsToRender();
      this.updateSections(addResult.sections, sectionsToRender);

      // Publish single cart update event for the entire replace operation with latest data
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'gift-manager',
        cartData: addResult,
        sections: addResult.sections,
        sectionsToRender: sectionsToRender
      });
    } catch (error) {
      // Disable loading on error
      this.disableGiftLoading(currentGift.variant_id);
      this.disableAllGiftLoading();
      throw error;
    }
  }

  /**
   * Update sections directly (for gift manager operations)
   * @param {Object} sections - Section HTML data
   * @param {Array} sectionsToRender - Sections configuration
   */
  updateSections(sections, sectionsToRender) {
    if (!sections || !sectionsToRender) return;

    sectionsToRender.forEach(section => {
      if (!sections[section.section]) return;

      if (section.id === 'cart-icon-bubble') {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const parser = new DOMParser();
          const sectionDoc = parser.parseFromString(sections[section.section], 'text/html');
          const shopifySection = sectionDoc.getElementById('shopify-section-cart-icon-bubble');
          if (shopifySection) {
            cartIconBubble.innerHTML = shopifySection.innerHTML;
          }
        }
      }
    });
  }

  /**
   * Handle cart update events from pub/sub system
   * @param {Object} event - Cart update event from pub/sub
   */
  async onCartUpdate(event) {
    // Skip if this update came from gift manager itself to avoid loops
    if (event.source === 'gift-manager') return;

    // Prevent race conditions - if already processing, skip this update
    if (this.isProcessing) return;

    // Check if we have a recently removed gift
    if (this.justRemovedProductGift) {
      console.log('Gift Manager: Found recently removed gift:', this.justRemovedProductGift);
    }

    // Get cart data to use
    const cartToUse = event.cartData && event.cartData.items && event.cartData.total_price !== undefined ? event.cartData : null;

    // Find eligible gift before updating to check if it matches removed gift
    const cartTotal = cartToUse ? this.calculateCartTotal(cartToUse) : 0;
    const eligibleGift = this.findEligibleGift(cartTotal);

    // If eligible gift matches recently removed gift, skip the update
    if (eligibleGift && this.justRemovedProductGift === true) {
      console.log('Gift Manager: Skipping update - eligible gift matches recently removed gift:', eligibleGift.variant);
      this.justRemovedProductGift = null; // Reset the flag
      return;
    }
    // if (eligibleGift && this.justRemovedProductGift && 
    //     eligibleGift.variant.toString() === this.justRemovedProductGift.toString()) {
    //   console.log('Gift Manager: Skipping update - eligible gift matches recently removed gift:', eligibleGift.variant);
    //   this.justRemovedProductGift = null; // Reset the flag
    //   return;
    // }

    await this.updateGifts(cartToUse);
  }



  /**
 * Check if a gift was manually removed by comparing current cart to previous cart
 * @returns {Promise<boolean>} - Returns true if a gift was manually removed
 */
  async checkForManuallyRemovedGifts() {
    if (!this.currentCart) return false;

    // Get fresh cart data
    const newCart = await this.fetchCart();

    console.log('Gift Manager: Checking for manual removal...');

    // Find if there was a gift in the previous cart
    console.log('Gift Manager: Checking PREVIOUS cart for gifts...');
    const previousGift = this.getCurrentGiftInCart(this.currentCart);

    console.log('Gift Manager: Checking CURRENT cart for gifts...');
    const currentGift = this.getCurrentGiftInCart(newCart);

    console.log('Gift Manager: Manual removal comparison:', {
      hadGiftBefore: !!previousGift,
      hasGiftNow: !!currentGift,
      previousGiftVariant: previousGift?.variant_id,
      currentGiftVariant: currentGift?.variant_id
    });

    let giftWasManuallyRemoved = false;

    // If there was a gift before but not now, check if it was manually removed
    if (previousGift && !currentGift) {
      // Check if this was an automatic removal (gift manager initiated)
      if (this.lastRemovalReason === 'auto') {
        // Reset the flag for next time
        this.lastRemovalReason = null;
      } else {
        // Add this gift variant to the removed list
        console.log('Gift Manager: Detected manual gift removal:', {
          cartItemVariantId: previousGift.variant_id,
          cartItemTitle: previousGift.title || previousGift.product_title,
          cartItemId: previousGift.id,
          cartItemKey: previousGift.key
        });
        await this.addToRemovedGifts(previousGift.variant_id);

        // Clear manual selection if the removed gift was manually selected
        const manualSelection = this.getManualGiftSelection();
        if (manualSelection && manualSelection.variantId.toString() === previousGift.variant_id.toString()) {
          console.log('Gift Manager: Clearing manual selection for removed gift:', {
            manualSelectionVariant: manualSelection.variantId,
            removedGiftVariant: previousGift.variant_id,
            match: true
          });
          await this.clearManualGiftSelection();
        } else if (manualSelection) {
          console.log('Gift Manager: Manual selection does not match removed gift:', {
            manualSelectionVariant: manualSelection.variantId,
            removedGiftVariant: previousGift.variant_id,
            match: false
          });
        }

        giftWasManuallyRemoved = true;
      }
    }

    // Update current cart reference
    this.currentCart = newCart;

    return giftWasManuallyRemoved;
  }



  /**
   * Save manual gift selection to cart attributes
   * @param {number} variantId - Selected gift variant ID
   * @returns {Promise}
   */
  async saveManualGiftSelection(variantId) {
    // Also save the cart total at time of selection to determine if higher tiers were available
    const currentCartTotal = this.currentCart ? this.calculateCartTotal(this.currentCart) : 0;

    await this.updateCartAttributes({
      '_selected_gift_variant': variantId.toString(),
      '_gift_selection_type': 'manual',
      '_selection_cart_total': currentCartTotal.toString()
    });
  }

  /**
   * Clear manual gift selection from cart attributes
   * @returns {Promise}
   */
  async clearManualGiftSelection() {
    await this.updateCartAttributes({
      '_selected_gift_variant': '',
      '_gift_selection_type': '',
      '_selection_cart_total': ''
    });
  }

  /**
   * Get manual gift selection from cart attributes
   * @returns {Object|null} - {variantId: number, type: string, selectionCartTotal: number} or null
   */
  getManualGiftSelection() {
    if (!this.currentCart?.attributes) return null;

    const variantId = this.currentCart.attributes['_selected_gift_variant'];
    const selectionType = this.currentCart.attributes['_gift_selection_type'];
    const selectionCartTotal = this.currentCart.attributes['_selection_cart_total'];

    if (selectionType === 'manual' && variantId) {
      return {
        variantId: parseInt(variantId),
        type: selectionType,
        selectionCartTotal: selectionCartTotal ? parseInt(selectionCartTotal) : 0
      };
    }

    return null;
  }

  /**
   * Check if a gift variant is manually selected
   * @param {number} variantId - Variant ID to check
   * @returns {boolean}
   */
  isManuallySelected(variantId) {
    const manualSelection = this.getManualGiftSelection();
    return manualSelection && manualSelection.variantId === variantId;
  }





  /**
   * Set CartItems instance reference for section rendering
   * @param {Object} cartItems - CartItems instance
   */
  setCartItems(cartItems) {
    this.cartItems = cartItems;
  }

  /**
   * Enable loading state for gift item
   * @param {string|number} variantId - Gift variant ID
   */
  enableGiftLoading(variantId) {
    // Find gift item in cart by checking line item data or properties
    const cartItems = document.querySelectorAll('.cart-item');
    cartItems.forEach(item => {
      // Check if this is a gift item by looking for various indicators
      const hasGiftProperty = item.querySelector('[data-properties*="_gift_product"]');
      const matchesVariant = item.dataset.variantId === variantId?.toString();
      const hasGiftClass = item.classList.contains('cart-item--gift');
      const hasGiftIndicator = item.querySelector('.cart-item__gift-indicator, .gift-item, [data-gift="true"]');

      // Also check if item contains gift text or has a gift property in any data attribute
      const dataAttributes = Array.from(item.attributes).some(attr =>
        attr.name.startsWith('data-') && attr.value.includes('_gift_product')
      );

      const isGiftItem = hasGiftProperty || matchesVariant || hasGiftClass || hasGiftIndicator || dataAttributes;

      if (isGiftItem) {
        // Add loading class and show loading overlay
        item.classList.add('cart-item--gift-loading');

        // Show loading overlay (rendered by Liquid)
        const loadingOverlay = item.querySelector('.gift-loading-overlay');
        if (loadingOverlay) {
          loadingOverlay.classList.remove('hidden');
        }
      }
    });
  }

  /**
   * Disable loading state for gift item
   * @param {string|number} variantId - Gift variant ID
   */
  disableGiftLoading(variantId) {
    // Find gift item in cart and disable gift loading
    const cartItems = document.querySelectorAll('.cart-item');
    cartItems.forEach(item => {
      // Check if this is a gift item that matches the variant
      const hasGiftProperty = item.querySelector('[data-properties*="_gift_product"]');
      const matchesVariant = item.dataset.variantId === variantId?.toString();
      const hasGiftClass = item.classList.contains('cart-item--gift');
      const dataAttributes = Array.from(item.attributes).some(attr =>
        attr.name.startsWith('data-') && attr.value.includes('_gift_product')
      );

      const isGiftItem = hasGiftProperty || matchesVariant || hasGiftClass || dataAttributes;

      if (isGiftItem) {
        // Remove loading class and hide loading overlay
        item.classList.remove('cart-item--gift-loading');

        // Hide loading overlay
        const loadingOverlay = item.querySelector('.gift-loading-overlay');
        if (loadingOverlay) {
          loadingOverlay.classList.add('hidden');
        }
      }
    });
  }

  /**
   * Enable loading for all gift items (when no specific variant known)
   */
  enableAllGiftLoading() {
    const cartItems = document.querySelectorAll('.cart-item');
    cartItems.forEach(item => {
      // Check if this is a gift item by looking for various indicators
      const hasGiftProperty = item.querySelector('[data-properties*="_gift_product"]');
      const hasGiftClass = item.classList.contains('cart-item--gift');
      const hasGiftIndicator = item.querySelector('.cart-item__gift-indicator, .gift-item, [data-gift="true"]');

      // Also check data attributes for gift properties
      const dataAttributes = Array.from(item.attributes).some(attr =>
        attr.name.startsWith('data-') && attr.value.includes('_gift_product')
      );

      const isGiftItem = hasGiftProperty || hasGiftClass || hasGiftIndicator || dataAttributes;

      if (isGiftItem) {
        // Add loading class and show loading overlay
        item.classList.add('cart-item--gift-loading');

        // Show loading overlay (rendered by Liquid)
        const loadingOverlay = item.querySelector('.gift-loading-overlay');
        if (loadingOverlay) {
          loadingOverlay.classList.remove('hidden');
        }
      }
    });
  }

  /**
   * Disable loading for all gift items
   */
  disableAllGiftLoading() {
    const cartItems = document.querySelectorAll('.cart-item');
    cartItems.forEach(item => {
      // Check if this is a gift item
      const hasGiftProperty = item.querySelector('[data-properties*="_gift_product"]');
      const hasGiftClass = item.classList.contains('cart-item--gift');
      const hasGiftIndicator = item.querySelector('.cart-item__gift-indicator, .gift-item, [data-gift="true"]');
      const dataAttributes = Array.from(item.attributes).some(attr =>
        attr.name.startsWith('data-') && attr.value.includes('_gift_product')
      );

      const isGiftItem = hasGiftProperty || hasGiftClass || hasGiftIndicator || dataAttributes;

      if (isGiftItem) {
        // Remove loading class and hide loading overlay
        item.classList.remove('cart-item--gift-loading');

        // Hide loading overlay
        const loadingOverlay = item.querySelector('.gift-loading-overlay');
        if (loadingOverlay) {
          loadingOverlay.classList.add('hidden');
        }
      }
    });
  }



  /**
   * Handle gift selection from modal buttons
   * @param {HTMLElement} button - The select button that was clicked
   * @returns {Promise}
   */
  async handleGiftSelection(button) {
    try {
      const variantId = button.dataset.giftVariant;
      const title = button.dataset.giftTitle;
      const minAmount = parseInt(button.dataset.giftMinAmount);
      const price = parseInt(button.dataset.giftPrice);

      if (!variantId) {
        console.error('No gift variant ID found');
        return;
      }

      // Show loading state
      button.textContent = window.giftStrings?.selecting || 'Selecting...';
      button.disabled = true;
      button.classList.add('loading');

      // Create gift object
      const selectedGift = {
        variant: parseInt(variantId),
        title: title,
        min_amount: minAmount,
        price: price
      };

      console.log('Gift Manager: Manual gift selection:', {
        buttonVariantId: variantId,
        parsedVariantId: parseInt(variantId),
        giftObject: selectedGift
      });

      // Remove this gift from removed list if it was previously removed
      // await this.removeFromRemovedGifts(parseInt(variantId));

      // Save manual selection to cart attributes
      await this.saveManualGiftSelection(parseInt(variantId));

      // Get current cart and gift
      const cart = await this.fetchCart();
      const currentGift = this.getCurrentGiftInCart(cart);

      // Replace or add the gift
      if (currentGift) {
        await this.replaceGift(currentGift, selectedGift, 'manual');
      } else {
        await this.addGiftToCart(selectedGift);
      }

      // Close modal after successful selection
      const modal = document.getElementById('GiftSelectionModal');
      if (modal && modal.hide) {
        modal.hide();
      }



    } catch (error) {
      console.error('Error selecting gift:', error);

      // Reset button state
      button.textContent = window.giftStrings?.select || 'Select';
      button.disabled = false;
      button.classList.remove('loading');


    }
  }

  /**
   * Initialize gift selection event listeners
   */
  initializeGiftSelection() {
    // Listen for gift selection button clicks
    document.addEventListener('click', (event) => {
      if (event.target.matches('.product__gifts-select-btn:not([disabled])')) {
        event.preventDefault();
        this.handleGiftSelection(event.target);
      }
    });
  }

  /**
   * Initialize and handle progress bar updates
   */
  initGiftsProgressBar() {
    const SELECTORS = {
      GIFTS_GRID: '[data-product-gifts]',
      PRODUCT_GIFTS: '.product__gifts',
      GIFTS_DOT: '.product__gifts-dot',
      LAST_DOT: '.product__gifts-item:last-child .product__gifts-dot'
    };

    const giftsGrid = document.querySelector(SELECTORS.GIFTS_GRID);
    const productGifts = document.querySelector(SELECTORS.PRODUCT_GIFTS);
    if (!giftsGrid || !productGifts) {
      return;
    }

    // Clean up existing listeners if they exist
    if (this.giftsResizeHandler) {
      window.removeEventListener('resize', this.giftsResizeHandler);
    }
    if (this.giftsObserver) {
      this.giftsObserver.disconnect();
    }

    const calculateProgress = () => {
      const { cartTotal, thresholds, dots, gridRect } = this.getProgressData(giftsGrid, SELECTORS);
      if (!cartTotal || !thresholds.length || !dots.length) return;

      const pixelProgress = thresholds.length === 1
        ? this.calculateSingleTierProgress(cartTotal, thresholds[0], dots[0], gridRect)
        : this.calculateMultiTierProgress(cartTotal, thresholds, dots, gridRect);

      this.setProgressStyles(pixelProgress, gridRect, giftsGrid, productGifts);
    };

    const calculateOffset = () => {
      const lastDot = giftsGrid.querySelector(SELECTORS.LAST_DOT);
      if (!lastDot) return;

      try {
        const gridRect = giftsGrid.getBoundingClientRect();
        const dotRect = lastDot.getBoundingClientRect();

        if (gridRect.width === 0 || dotRect.width === 0) return;

        const dotCenter = dotRect.left + (dotRect.width / 2);
        const gridRight = gridRect.right;
        const offsetDistance = Math.max(0, gridRight - dotCenter);

        productGifts.style.setProperty('--gifts-end-offset', `${offsetDistance}px`);
      } catch (error) {
        console.warn('Error calculating gifts offset:', error);
      }
    };

    const updateAll = () => {
      calculateProgress();
      calculateOffset();
    };

    // Initial update
    setTimeout(updateAll, 100);

    // Set up resize listener with debouncing
    let resizeTimeout;
    this.giftsResizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateAll, 150);
    };
    window.addEventListener('resize', this.giftsResizeHandler);

    // Set up mutation observer for DOM changes
    this.giftsObserver = new MutationObserver(() => {
      setTimeout(updateAll, 100);
    });

    const observeTarget = giftsGrid.parentElement;
    if (observeTarget) {
      this.giftsObserver.observe(observeTarget, {
        childList: true,
        subtree: true
      });
    }

    // Store references for cleanup
    this.giftsGrid = giftsGrid;
    this.productGifts = productGifts;
  }

  /**
   * Get progress bar calculation data
   */
  getProgressData(giftsGrid, SELECTORS) {
    // Always use fresh cart total if available
    let cartTotal = parseInt(giftsGrid.dataset.cartTotal || '0');

    // Fallback to calculating from current cart if dataset is missing or outdated
    if (this.currentCart && (cartTotal === 0 || !giftsGrid.dataset.cartTotal)) {
      cartTotal = this.calculateCartTotal(this.currentCart);
      giftsGrid.dataset.cartTotal = cartTotal.toString();
    }

    const thresholdsStr = giftsGrid.dataset.giftThresholds || '';
    const thresholds = thresholdsStr.split(',').map(t => parseInt(t)).filter(t => !isNaN(t));
    const dots = giftsGrid.querySelectorAll(SELECTORS.GIFTS_DOT);
    const gridRect = giftsGrid.getBoundingClientRect();

    return { cartTotal, thresholds, dots, gridRect };
  }

  /**
   * Calculate progress for single tier
   */
  calculateSingleTierProgress(cartTotal, threshold, firstDot, gridRect) {
    const dotRect = firstDot.getBoundingClientRect();
    const dotCenter = dotRect.left + (dotRect.width / 2);
    const totalDistance = dotCenter - gridRect.left;

    if (totalDistance <= 0 || threshold <= 0) return 0;

    const progress = Math.min(1, cartTotal / threshold);
    return progress * totalDistance;
  }

  /**
   * Calculate progress for multiple tiers
   */
  calculateMultiTierProgress(cartTotal, thresholds, dots, gridRect) {
    const dotPositions = Array.from(dots).map(dot => {
      const dotRect = dot.getBoundingClientRect();
      return dotRect.left + (dotRect.width / 2) - gridRect.left;
    });

    // Check if we've completed all segments
    if (cartTotal >= thresholds[thresholds.length - 1]) {
      return dotPositions[dotPositions.length - 1];
    }

    // Find the first threshold we haven't reached yet
    const targetIndex = thresholds.findIndex(threshold => cartTotal < threshold);

    if (targetIndex === -1) {
      return dotPositions[dotPositions.length - 1]; // Fallback to last dot
    }

    const currentThreshold = thresholds[targetIndex];
    const previousThreshold = targetIndex > 0 ? thresholds[targetIndex - 1] : 0;
    const currentDotPos = dotPositions[targetIndex];
    const previousDotPos = targetIndex > 0 ? dotPositions[targetIndex - 1] : 0;

    // If we haven't reached the previous threshold, we're before this segment
    if (cartTotal <= previousThreshold) {
      return previousDotPos;
    }

    // We're partially through this segment
    const monetaryProgress = cartTotal - previousThreshold;
    const monetaryRange = currentThreshold - previousThreshold;
    const pixelRange = currentDotPos - previousDotPos;

    if (monetaryRange > 0 && pixelRange > 0) {
      const segmentProgress = monetaryProgress / monetaryRange;
      return previousDotPos + (segmentProgress * pixelRange);
    }

    return previousDotPos;
  }

  /**
   * Set progress bar CSS styles
   */
  setProgressStyles(pixelProgress, gridRect, giftsGrid, productGifts) {
    // Get available width (considering end offset)
    const endOffset = parseFloat(productGifts.style.getPropertyValue('--gifts-end-offset').replace('px', '')) || 0;
    const availableWidth = gridRect.width - endOffset;

    // Ensure progress doesn't exceed available width
    const progressWidthPx = Math.min(pixelProgress, availableWidth);

    // Calculate percentage for backward compatibility
    const progressPercentage = gridRect.width > 0 ? (pixelProgress / gridRect.width) * 100 : 0;

    // Set CSS custom properties
    giftsGrid.style.setProperty('--progress-width', Math.min(100, progressPercentage));
    giftsGrid.style.setProperty('--progress-width-px', `${progressWidthPx}px`);
  }

  /**
   * Update progress bar manually (called after cart changes)
   */
  updateProgressBar() {
    // Always try to find the elements fresh in case DOM was updated
    const giftsGrid = document.querySelector('[data-product-gifts]');
    const productGifts = document.querySelector('.product__gifts');

    if (giftsGrid && productGifts && this.currentCart) {
      // Update cart total in dataset before recalculating
      const cartTotal = this.calculateCartTotal(this.currentCart);
      giftsGrid.dataset.cartTotal = cartTotal.toString();

      // Store references for future use
      this.giftsGrid = giftsGrid;
      this.productGifts = productGifts;

      // Recalculate progress
      this.initGiftsProgressBar();
    }
  }



  /**
   * Initialize gift management
   * @param {Object} initialCart - Optional initial cart data
   */
  async initialize(initialCart = null) {
    await this.updateGifts(initialCart);
    this.initializeGiftSelection();
    this.initGiftsProgressBar();
  }

  /**
   * Cleanup event listeners
   */
  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    // Clean up progress bar listeners
    if (this.giftsResizeHandler) {
      window.removeEventListener('resize', this.giftsResizeHandler);
    }
    if (this.giftsObserver) {
      this.giftsObserver.disconnect();
    }
  }
}

// Create global instance
window.giftManager = new GiftManager();

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.giftManager.initialize();
  });
} else {
  window.giftManager.initialize();
}
